import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { GvcCreate } from "../../schema/types/gvc/create";
import { Gvc, LocationOptionsItem } from "../../schema/types/gvc/entity";
import { GvcInput } from "../../schema/types/gvc/input";
import { getDefaultQueryInput, inputToQuery, queryToInput } from "../../schema/types/query/input";
import { Tracing, TracingCustomTag } from "../../schema/types/tracing/entity";
import { TracingInput } from "../../schema/types/tracing/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { v4 as uuidv4 } from "uuid";
import { envToInput, inputToEnv } from "../workload/draft/utils/envVar";
import { defaultValues } from "../../services/defaults";
import { getDefaultSidecarInput, inputToSidecar, sidecarToInput } from "../../mst/stores/cpln.jwt.client.draft";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";

export function getDefaultTracingInput(): TracingInput {
  return {
    isEnabled: false,
    provider: "controlplane",
    customTags: [],
    sampling: defaultValues.tracing.sampling.default,
    otel: {
      endpoint: "",
    },
    lightstep: {
      credentialsSecret: getDefaultKindSelectState(),
      endpoint: "",
    },
  };
}
export function getDefaultGvcInput(): GvcInput {
  const df = defaultValues.gvc;

  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    endpointNamingFormat: df.endpointNamingFormat.default as any,
    env: [],
    keda: {
      isEnabled: false,
      identity: getDefaultKindSelectState(),
      secrets: [],
    },
    loadBalancer: {
      isEnabled: false,
      ipSet: getDefaultKindSelectState(),
      multizone: {
        isEnabled: false,
      },
      redirect: {
        class: {
          status401: "",
          status5xx: "",
        },
      },
      trustedProxies: df.loadBalancer.trustedProxies.default,
    },
    locationQuery: getDefaultQueryInput(),
    locationItems: [],
    locationOptions: [],
    pullSecretItems: [],
    tracing: getDefaultTracingInput(),
    sidecar: getDefaultSidecarInput(),
    meta: {
      isEdit: false,
      queryResult: {
        location: {
          data: null,
          isCollapsed: true,
        },
      },
      secretCache: {},
    },
  };
}

export function inputToTracing(input: TracingInput): Tracing | undefined {
  if (!input.isEnabled) {
    return;
  }
  const res: Tracing = {
    sampling: Number(input.sampling),
  };
  const customTags: Record<string, TracingCustomTag> = {};
  for (let tag of input.customTags) {
    customTags[tag.name] = { literal: { value: tag.value } };
  }
  res.customTags = customTags;

  if (input.provider === "controlplane") {
    res.provider = { controlplane: {} };
  } else if (input.provider === "lightstep") {
    res.provider = { lightstep: { endpoint: input.lightstep.endpoint } };
    if (input.lightstep.credentialsSecret.value) {
      res.provider!.lightstep!.credentials = input.lightstep.credentialsSecret.link;
    }
  } else if (input.provider === "otel") {
    res.provider = { otel: { endpoint: input.otel.endpoint } };
  }
  return res;
}

export function inputToGvc(input: GvcInput): GvcCreate {
  const res: GvcCreate = {
    name: input.name,
    kind: "gvc",
    tags: inputToTags(input.tags),
    spec: {
      endpointNamingFormat: input.endpointNamingFormat,
      env: inputToEnv(input.env),
      keda: {
        enabled: input.keda.isEnabled,
      },
      staticPlacement: {
        locationLinks: input.locationItems.map((l) => l.link),
      },
      pullSecretLinks: input.pullSecretItems.map((l) => l.link),
    },
  };

  // locationOptions: include all items as-is, validation guards submission
  const locationOptions: LocationOptionsItem[] = input.locationOptions.map((opt) => {
    const item: LocationOptionsItem = { locationLink: opt.locationLink };
    if (opt.routingTier !== "") item.routingTier = Number(opt.routingTier);
    if (opt.latencyOffsetMs !== "") item.latencyOffsetMs = Number(opt.latencyOffsetMs);
    if (opt.latencyToleranceMs !== "") item.latencyToleranceMs = Number(opt.latencyToleranceMs);
    return item;
  });
  if (locationOptions.length > 0) {
    res.spec!.staticPlacement!.locationOptions = locationOptions;
  }

  if (input.keda.isEnabled) {
    if (input.keda.identity.link) {
      res.spec!.keda!.identityLink = input.keda.identity.link;
    }
    if (input.keda.secrets.length > 0) {
      res.spec!.keda!.secrets = input.keda.secrets.map((s) => s.link);
    }
  }
  if (input.locationQuery.isEnabled) {
    res.spec!.staticPlacement!.locationQuery = inputToQuery(input.locationQuery, "location");
  }

  if (input.loadBalancer.isEnabled) {
    res.spec!.loadBalancer = {
      dedicated: true,
      multiZone: {
        enabled: input.loadBalancer.multizone.isEnabled,
      },
    };

    if (input.loadBalancer.ipSet.value) {
      res.spec!.loadBalancer.ipSet = input.loadBalancer.ipSet.link;
    }

    if (input.loadBalancer.trustedProxies.length > 0) {
      res.spec!.loadBalancer.trustedProxies = Number(input.loadBalancer.trustedProxies);
    }

    if (input.loadBalancer.redirect.class.status401 || input.loadBalancer.redirect.class.status5xx) {
      res.spec!.loadBalancer.redirect = {
        class: {},
      };
      if (input.loadBalancer.redirect.class.status401) {
        res.spec!.loadBalancer.redirect.class!.status401 = input.loadBalancer.redirect.class.status401;
      }
      if (input.loadBalancer.redirect.class.status5xx) {
        res.spec!.loadBalancer.redirect.class!.status5xx = input.loadBalancer.redirect.class.status5xx;
      }
    }
  }

  const tracing = inputToTracing(input.tracing);
  if (tracing) {
    res.spec!.tracing = tracing;
  }

  const sidecar = inputToSidecar(input.sidecar);
  if (sidecar) {
    res.spec!.sidecar = sidecar;
  }

  // Preserve deprecated domain field if it exists
  if (input.domain) {
    res.spec!.domain = input.domain;
  }

  if (input.description) {
    res.description = input.description;
  }
  return res;
}

export function tracingToInput(tracing?: Tracing): TracingInput {
  const res = getDefaultTracingInput();
  if (!tracing || !tracing.provider || Object.keys(tracing.provider).length < 1) {
    return res;
  }

  res.isEnabled = true;
  if (tracing.sampling !== undefined) {
    res.sampling = String(tracing.sampling);
  }
  res.customTags = Object.keys(tracing.customTags || {}).map((key) => {
    const value = tracing!.customTags![key];
    return { id: uuidv4(), name: key, value: String(value.literal?.value || "") };
  });
  if (tracing.provider.controlplane) {
    res.provider = "controlplane";
  }
  if (tracing.lightstep) {
    res.provider = "lightstep";
    res.lightstep.endpoint = tracing.lightstep.endpoint;
    if (tracing.lightstep.credentials) {
      const { name, absolute } = parseLink(tracing.lightstep.credentials, { useInputCtx: true });
      res.lightstep.credentialsSecret = {
        value: name,
        link: absolute,
      };
    }
  }
  if (tracing.provider?.lightstep) {
    res.provider = "lightstep";
    res.lightstep.endpoint = tracing.provider.lightstep.endpoint;
    if (tracing.provider.lightstep.credentials) {
      const { name, absolute } = parseLink(tracing.provider.lightstep.credentials, { useInputCtx: true });
      res.lightstep.credentialsSecret = {
        value: name,
        link: absolute,
      };
    }
  }
  if (tracing.provider?.otel) {
    res.provider = "otel";
    res.otel.endpoint = tracing.provider.otel.endpoint;
  }
  return res;
}

export function gvcToInput(gvc: Gvc): GvcInput {
  const def = getDefaultGvcInput();
  const res: GvcInput = {
    ...def,
    id: gvc.id,
    version: gvc.version,
    name: gvc.name,
    description: gvc.description,
    tags: tagsToInput(gvc.tags),
    env: envToInput(gvc.spec.env || []),
    locationItems: (gvc.spec.staticPlacement?.locationLinks || []).map((locationLink) => {
      const { name, absolute } = parseLink(locationLink, { useInputCtx: true });
      return {
        value: name,
        name: name,
        link: absolute,
      };
    }),
    locationOptions: (gvc.spec.staticPlacement?.locationOptions || []).map((opt) => ({
      id: uuidv4(),
      locationLink: opt.locationLink,
      routingTier: opt.routingTier !== undefined ? String(opt.routingTier) : "",
      latencyOffsetMs: opt.latencyOffsetMs !== undefined ? String(opt.latencyOffsetMs) : "",
      latencyToleranceMs: opt.latencyToleranceMs !== undefined ? String(opt.latencyToleranceMs) : "",
    })),
    pullSecretItems: (gvc.spec.pullSecretLinks || []).map((secretLink) => {
      const { name, absolute } = parseLink(secretLink, { useInputCtx: true });
      return {
        value: name,
        name: name,
        link: absolute,
      };
    }),
    locationQuery: queryToInput(gvc.spec.staticPlacement?.locationQuery),
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };
  let endpointNamingFormat = gvc.spec.endpointNamingFormat || "legacy";
  if (endpointNamingFormat === "default") {
    endpointNamingFormat = "legacy";
  }
  res.endpointNamingFormat = endpointNamingFormat;
  if (gvc.spec.keda?.enabled) {
    res.keda.isEnabled = true;
    if (gvc.spec.keda?.identityLink) {
      const { name, absolute } = parseLink(gvc.spec.keda?.identityLink, { useInputCtx: true });
      res.keda.identity = {
        value: name,
        link: absolute,
      };
    }
    res.keda.secrets = (gvc.spec.keda.secrets || []).map((l) => {
      const { name, absolute } = parseLink(l, { useInputCtx: true });
      return {
        value: name,
        link: absolute,
      };
    });
  }
  if (gvc.spec.loadBalancer?.dedicated) {
    res.loadBalancer.isEnabled = true;
    if (gvc.spec.loadBalancer?.ipSet) {
      const { name, absolute } = parseLink(gvc.spec.loadBalancer?.ipSet, { useInputCtx: true });
      res.loadBalancer.ipSet = {
        value: name,
        link: absolute,
      };
    }
    if (gvc.spec.loadBalancer.multiZone?.enabled) {
      res.loadBalancer.multizone.isEnabled = true;
    }
    if (gvc.spec.loadBalancer.redirect?.class?.status401) {
      res.loadBalancer.redirect.class.status401 = gvc.spec.loadBalancer.redirect.class.status401;
    }
    if (gvc.spec.loadBalancer.redirect?.class?.status5xx) {
      res.loadBalancer.redirect.class.status5xx = gvc.spec.loadBalancer.redirect.class.status5xx;
    }
    // Reset to empty string to avoid default "0" leaking for GVCs without trustedProxies
    res.loadBalancer.trustedProxies = "";
    if (gvc.spec.loadBalancer.trustedProxies !== undefined) {
      res.loadBalancer.trustedProxies = String(gvc.spec.loadBalancer.trustedProxies);
    }
  }

  res.tracing = tracingToInput(gvc.spec.tracing);

  const sidecar = sidecarToInput(gvc.spec.sidecar);
  if (sidecar) {
    res.sidecar = sidecar;
  }

  // Preserve deprecated domain field if it exists
  if (gvc.spec.domain !== undefined) {
    res.domain = gvc.spec.domain;
  }

  return res;
}

export function gvcToPatch(_gvc: GvcCreate, concurrency: OptimisticConcurrency) {
  const gvc: any = JSON.parse(JSON.stringify(_gvc));

  gvc[`$replace/spec`] = gvc.spec;
  delete gvc.spec;

  gvc[`$replace/tags`] = gvc.tags;
  delete gvc.tags;

  gvc.id = concurrency.id;
  gvc.version = concurrency.version;

  return gvc;
}
